import { Button, Center, Code, Loader, Stack, Text, Title } from "@mantine/core";
import { useEffect, useState } from "react";
import { useRouteError } from "react-router";

const RELOAD_KEY = "ct.reloadedForChunk";

/** A page's code failed to load: usually a new version was deployed (or the dev server
 *  restarted) and this tab still points at old files. */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|error loading dynamically/i.test(
    message,
  );
}

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    return Date.now() - at < 10_000;
  } catch {
    return true; // Can't remember; don't risk a reload loop.
  }
}

export function RouteError() {
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);
  // Decided once: reload automatically, unless we just did (avoids a reload loop).
  const [autoReload] = useState(() => chunkError && !reloadedRecently());

  useEffect(() => {
    if (!autoReload) return;
    try {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      // Reload anyway; reloadedRecently() treats missing storage as "just reloaded".
    }
    window.location.reload();
  }, [autoReload]);

  if (autoReload) {
    return (
      <Center mih="60dvh">
        <Loader />
      </Center>
    );
  }

  const message = error instanceof Error ? error.message : String(error);
  return (
    <Center mih="60dvh" p="md">
      <Stack align="center" maw={480}>
        <Title order={3}>Something went wrong</Title>
        <Text c="dimmed" ta="center">
          {chunkError
            ? "A newer version of the app may be available."
            : "This page hit an unexpected error."}
        </Text>
        <Code block style={{ whiteSpace: "pre-wrap", maxWidth: "100%" }}>
          {message}
        </Code>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </Stack>
    </Center>
  );
}
